import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import classnames from "classnames";
import { useStore } from "shared/hooks";
import { CompanyBestDesignerModel } from "shared/models";
import { companyBestDesignerQuery } from "shared/queries";
import { UiAvatar, UiDataBoundary, UiGrid, UiNavLink, UiIcon, UiModal } from "shared/uikit";
import { ROUTES, ICONS } from "shared/constants";
import { ApplicationModule } from "shared/modules";

import './style.scss';

const TAB_MODAL = {
    LEADER: "leader",
    USER: "user"
}

export const PCompanyBestDesigner = observer(() => {
    const store = useStore(() => ({
        isLoading: false,
        isSuccess: false,
        items: [] as CompanyBestDesignerModel[],
        isOpen: false,
        activeTab: TAB_MODAL.LEADER
    }));

    useEffect(() => {
        (async () => {
            store.set('isLoading', true);
            const { isSuccess, data } = await companyBestDesignerQuery();
            if (isSuccess && data) {
                store.set("items", data.items?.map(item => new CompanyBestDesignerModel(item)));
            }
            store.set("isSuccess", isSuccess && data);
            store.set('isLoading', false);
        })();
    }, [store]);

    const refFirstUser = useRef<HTMLDivElement>(null);
    const refUser = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (store.activeTab === TAB_MODAL.USER) {
            refUser.current?.scrollIntoView({
                behavior: "smooth",
                block: "center"
            });
        }
        if (store.activeTab === TAB_MODAL.LEADER) {
            refFirstUser.current?.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
        }
    }, [store.activeTab]);

    const handleClick = (tab: string) => {
        if (TAB_MODAL.LEADER === tab) {
            store.set('activeTab', tab)
        }

        if (TAB_MODAL.USER === tab) {
            store.set('activeTab', tab)
        }
    }

    const getIconStar = (position: number) => {
        switch (position) {
            case 1:
                return "TINNET_STAR_GOLD"
            case 2:
                return "TINNET_STAR_SILVER"
            case 3:
                return "TINNET_STAR_BRONZE"
            default:
                return "";
        }
    }

    const currentUserId = ApplicationModule.user.id;
    const topDesignerItems = store.items.slice(0, 12);
    const currentUserItem = store.items.find(item => item.user.id === currentUserId);
    const previewItems = currentUserItem && !topDesignerItems.some(item => item.user.id === currentUserId)
        ? [...store.items.slice(0, 11), currentUserItem]
        : topDesignerItems;

    return (
        <UiDataBoundary isLoading={store.isLoading} isError={!store.isSuccess}>
            {
                store.items?.length ? (
                    <div className="p-company-best-designer">
                        <div className="p-company-best-designer__title">Рейтинг дизайнеров</div>
                        <div className="p-company-best-designer__wrapper">
                            <div className={classnames("p-company-best-designer__group", {"p-company-best-designer__group--two-column": previewItems.length > 5})}>
                                {previewItems.map((item) => (
                                    <div
                                        className={classnames("p-company-best-designer-user", { "is-user": currentUserId === item.user.id })}
                                        key={item.user.id}
                                    >
                                        <UiNavLink
                                            to={ROUTES.USER(item.user.slug)}
                                            className="p-company-best-designer-user__link"
                                        >
                                            <div className="p-company-best-designer-user__tinet">
                                                <span>
                                                    {item.tinetsCount}
                                                </span>
                                            </div>
                                            <div className="p-company-best-designer-user__avatar">
                                                <UiAvatar size={32} label={item.user.previewName} image={item.user.imageAvatar}/>
                                            </div>
                                            <div className="p-company-best-designer-user__info">
                                                <div className="p-company-best-designer-user__name">
                                                    {currentUserId === item.user.id ? "Ваша позиция" : item.user.previewName}
                                                </div>
                                                <div className="p-company-best-designer-user__level">
                                                    Дизайнер {item.level}-го уровня
                                                </div>
                                            </div>
                                            {getIconStar(item.ratingPosition) ?
                                                <UiIcon
                                                    size={24}
                                                    icon={ICONS[getIconStar(item.ratingPosition) as keyof typeof ICONS]}
                                                />
                                                : null}
                                        </UiNavLink>
                                    </div>
                                ))}
                            </div>
                            <div className="p-company-best-designer__more" onClick={() => store.set('isOpen', true)}>
                                Подробнее
                                <UiIcon
                                    size={12}
                                    icon={ICONS["ARROW_UP_RIGHT"]}
                                />
                            </div>
                        </div>

                        <UiModal isOpened={store.isOpen} onClose={() => store.set('isOpen', false)} className={"ui-modal-best-designer"}>
                            <div className="modal-best-designer">
                                <div className="modal-best-designer__title">Рейтинг дизайнеров</div>

                                <div className="modal-best-designer__tab">
                                    <div className={classnames("modal-best-designer__btn", { "active" : store.activeTab === TAB_MODAL.LEADER })} onClick={() => handleClick(TAB_MODAL.LEADER)}>Лидеры</div>
                                    <div className={classnames("modal-best-designer__btn", { "disabled": currentUserItem ? false : true }, { "active" : store.activeTab === TAB_MODAL.USER })} onClick={() => handleClick(TAB_MODAL.USER)}>Мое место</div>
                                </div>

                                <div className="modal-best-designer__list">
                                    {store.items.map((item, index) => (
                                        <div
                                            className={classnames("p-company-best-designer-user", { "is-user": currentUserId === item.user.id })}
                                            key={item.user.id}
                                            ref={currentUserId === item.user.id ? refUser : index === 0 ? refFirstUser : null}
                                        >
                                            <UiNavLink
                                                to={ROUTES.USER(item.user.slug)}
                                                className="p-company-best-designer-user__link"
                                            >
                                                <div className="p-company-best-designer-user__tinet">
                                                    <span>
                                                        {item.tinetsCount}
                                                    </span>
                                                </div>
                                                <div className="p-company-best-designer-user__avatar">
                                                    <UiAvatar size={32} label={item.user.previewName} image={item.user.imageAvatar}/>
                                                </div>
                                                <div className="p-company-best-designer-user__info">
                                                    <div className="p-company-best-designer-user__name">
                                                        {currentUserId === item.user.id ? "Ваша позиция" : item.user.previewName}
                                                    </div>
                                                    <div className="p-company-best-designer-user__level">
                                                        Дизайнер {item.level}-го уровня
                                                    </div>
                                                </div>
                                                {getIconStar(item.ratingPosition) ?
                                                    <UiIcon
                                                        size={24}
                                                        icon={ICONS[getIconStar(item.ratingPosition) as keyof typeof ICONS]}
                                                    />
                                                    : null}
                                            </UiNavLink>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </UiModal>
                    </div>
                ) :''
            }
        </UiDataBoundary>
    );
})
